import React, { useReducer } from 'react';
import startOfDay from 'date-fns/startOfDay';
import useStepper from 'state/hooks/use-stepper';
import useFormMap, { MaybeErrorMap } from 'state/hooks/use-form-map';
import { swap } from 'util/ordering-util';
import useForm from 'state/hooks/use-form';
import {
  ActiveFormVariant,
  LessonAssignmentState,
  StepTwoForm,
  StepOneFormMap,
  AssignmentKey
} from './types';
import {
  stepOneValidationSchema,
  getDefaultStepTwoFormState,
  getDefaultLessonAssignmentState
} from './util';
import { formatFullDate, formatUiDate } from 'util/date-util';
import { calcDuration, getActivityId } from 'util/assignment-util';
import { AssignmentEngineParams } from 'state/providers/LessonAssignmentProvider/types';

type LessonAssignmentAction =
  | {
      type: 'SET_ACTIVE_FORM';
      payload: { id: string; variant: ActiveFormVariant };
    }
  | {
      type: 'SET_SELECTED_STUDENTS_AND_CLASSROOMS';
      payload: { studentsAndClassrooms: any[] };
    }
  | {
      type: 'SET_DATE_RANGE';
      payload: string;
    }
  | {
      type: 'SET_ALL_CLASSROOMS_AND_STUDENTS';
      payload: { classrooms: any[]; students?: any[] };
    }
  | { type: 'TOGGLE_EDIT_TITLE' };

const lessonAssignmentReducer = (
  state: LessonAssignmentState,
  action: LessonAssignmentAction
): LessonAssignmentState => {
  switch (action.type) {
    case 'SET_ACTIVE_FORM':
      return {
        ...state,
        lastActiveFormId: state.activeFormId,
        activeFormId: String(action.payload.id),
        activeFormVariant: action.payload.variant
      };
    case 'TOGGLE_EDIT_TITLE':
      return { ...state, isEditingTitle: !state.isEditingTitle };
    case 'SET_SELECTED_STUDENTS_AND_CLASSROOMS':
      return {
        ...state,
        studentsAndClassrooms: action.payload.studentsAndClassrooms
      };
    case 'SET_ALL_CLASSROOMS_AND_STUDENTS': {
      const students = action.payload.classrooms?.reduce((acc: any, { id, students = [] }) => {
        acc.push(
          ...students.map(student => ({
            value: { ...student, classroomId: id },
            label: `${student.firstName} ${student.lastName}`,
            id: 'id'
          }))
        );
        return acc;
      }, []);

      return {
        ...state,
        classrooms: action.payload.classrooms?.map(x => ({ value: x, label: x.name, id: 'id' })),
        students
      };
    }
    case 'SET_DATE_RANGE':
      return {
        ...state,
        dateRange: action.payload
      };
    default:
      return getDefaultLessonAssignmentState();
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LessonAssignmentContext = React.createContext<any>([]);

export const LessonAssignmentProvider = (props: any) => {
  const [state, dispatch] = useReducer<
    React.Reducer<LessonAssignmentState, LessonAssignmentAction>
  >(lessonAssignmentReducer, getDefaultLessonAssignmentState());
  const [stepperState, stepperDispatch] = useStepper(3);

  const [stepOneFormState, stepOneFormDispatch] = useFormMap<StepOneFormMap>({
    initialFormValue: null,
    validationSchema: stepOneValidationSchema
  });

  const [stepTwoFormState, stepTwoFormDispatch] = useForm<StepTwoForm>({
    initialFormValue: getDefaultStepTwoFormState()
  });

  const onRemoveAssignment = () => {}; // TODO

  const getTeacherInstructionPayload = async (assignments, updating) =>
    assignments.reduce((acc, { id, teachingOrderLessonId = AssignmentEngineParams.BLANK }) => {
      const form = stepOneFormState.form[updating ? id : teachingOrderLessonId];
      const { audioFiles } = form;
      if (!audioFiles.length) return acc;
      acc.push({
        assignmentId: id,
        audioFiles
      });
      return acc;
    }, []);

  const getAssignmentData = ({ startDate, dueDate, instructions, lesson, title }: any) => ({
    teachingOrderLessonId: lesson.teachingOrderLessonId,
    title: title ?? lesson.title,
    pageId: lesson.pageId,
    maxAttempts: 1, // TODO once we have in another section
    dateAssigned: formatFullDate(startOfDay(new Date(startDate))),
    dateDue: formatFullDate(startOfDay(new Date(dueDate))),
    ...(instructions.length && { instructions }),
    sections: lesson.sections.reduce((acc, { title, type, activities, id }) => {
      if (!activities.length) return acc;
      acc.push({
        id,
        title,
        type,
        activities: activities.map(({ pageId, resourceId, clickAwayId, customContentId }) => ({
          pageId,
          resourceId,
          clickAwayId,
          customContentId
        }))
      });
      return acc;
    }, [])
  });

  const getAssignmentPayload = () =>
    Object.values(stepOneFormState.form).reduce((acc: any, assignment: any) => {
      if (!assignment.assigned) return acc;
      const assignmentData = getAssignmentData(assignment);
      acc.push(
        ...state.studentsAndClassrooms.map(({ value, students }) => ({
          ...assignmentData,
          classroomId: value.id,
          ...(students
            ? { studentIds: students.map(s => s.value.id) }
            : { studentIds: value.studentIds })
        }))
      );
      return acc;
    }, []);

  const onMoveActivity = (fromIndices, targetIndices) => {
    const fromActivities: any[] =
      (stepOneFormState.form?.[state.activeFormId] as any)?.lesson.sections[fromIndices.col]
        .activities ?? [];

    // move activity to same section
    if (fromIndices.col === targetIndices.col) {
      const activities = swap(fromActivities, fromIndices.item, targetIndices.item);

      const newStepOneLessonState = {
        ...stepOneFormState.form?.[state.activeFormId].lesson
      };

      newStepOneLessonState.sections[targetIndices.col].activities = activities;
      return stepOneFormDispatch.onFormChange(state.activeFormId as string)(
        AssignmentKey.LESSON,
        newStepOneLessonState ?? []
      );
    }

    // TODO revisit move activity to different section
    // const nextFromActivities = fromActivities.filter(x => x.id === activity.id);
    // const nextTargetActivities = targetActivities.splice(newIdx, 0, activity);

    // stepOneFormDispatch.onFormChange(state.activeFormId as string)(
    //   AssignmentKey.LESSON,
    //   nextFromActivities
    // );
    // stepOneFormDispatch.onFormChange(state.activeFormId as string)(
    //   AssignmentKey.LESSON,
    //   nextTargetActivities
    // );
  };

  const onAddActivity = (sectionIdx: number, newActivities) => {
    const newStepOneLessonState = {
      ...stepOneFormState.form?.[state.activeFormId].lesson
    };

    const addedActivities = [
      ...(newStepOneLessonState ? newStepOneLessonState.sections[sectionIdx].activities : []),
      ...newActivities
    ];
    newStepOneLessonState.sections[sectionIdx].activities = addedActivities;
    stepOneFormDispatch.onFormChange(state.activeFormId as string)(
      AssignmentKey.LESSON,
      newStepOneLessonState
    );
  };

  const onRemoveActivity = (sectionIdx: number, activityId, activitySection) => {
    const currentActivities = (
      stepOneFormState.form?.[state.activeFormId] as any
    )?.lesson?.sections[sectionIdx].activities.filter((x: any) => getActivityId(x) !== activityId);
    const newStepOneLessonState = {
      ...stepOneFormState.form?.[state.activeFormId].lesson
    };
    newStepOneLessonState.sections[sectionIdx].activities = currentActivities;

    stepOneFormDispatch.onFormChange(state.activeFormId as string)(
      activitySection,
      newStepOneLessonState ?? []
    );
  };

  const onRemoveFileAdded = name => {
    const audioFiles = stepOneFormState.form?.[state.activeFormId].audioFiles.filter(
      x => x.name !== name
    );

    stepOneFormDispatch.onFormChange(state.activeFormId as string)(
      AssignmentKey.AUDIO_FILES,
      audioFiles ?? []
    );
  };
  const onUnassignActiveForm = id => {
    stepOneFormDispatch.onFormChange(id)(AssignmentKey.ASSIGNED, false);
  };

  const handleAssign = id => {
    stepOneFormDispatch.onFormChange(id)(AssignmentKey.ASSIGNED, true);
  };

  const onRemoveAllActivities = (sectionIdx: number, activitySection) => {
    const newStepOneLessonState = {
      ...stepOneFormState.form?.[state.activeFormId].lesson
    };
    newStepOneLessonState.sections[sectionIdx].activities = [];
    stepOneFormDispatch.onFormChange(state.activeFormId as string)(
      activitySection,
      newStepOneLessonState ?? []
    );
  };

  const setActiveForm = async (
    id: string,
    variant: ActiveFormVariant = ActiveFormVariant.EDITABLE
  ): Promise<MaybeErrorMap> => {
    if (!state.activeFormId) {
      dispatch({ type: 'SET_ACTIVE_FORM', payload: { id, variant } });
      return null;
    }

    const errors = await stepOneFormDispatch.validateSingleForm(
      state.activeFormId as string,
      stepOneFormState.form?.[state.activeFormId as string]
    );

    if (!errors) {
      dispatch({ type: 'SET_ACTIVE_FORM', payload: { id, variant } });
    }

    return errors;
  };

  const setSelectedClassroomsAndStudents = async (studentsAndClassrooms: any) => {
    if (studentsAndClassrooms) {
      dispatch({
        type: 'SET_SELECTED_STUDENTS_AND_CLASSROOMS',
        payload: { studentsAndClassrooms }
      });

      return null;
    }
  };

  const toggleEditTitle = () => dispatch({ type: 'TOGGLE_EDIT_TITLE' });

  // overrides
  const stepOneOnFormChange = (name, value) => {
    stepOneFormDispatch.onFormChange(state.activeFormId as string)(name, value);
  };

  const stepTwoOnMultiSelectFormChange = (name: 'classroomIds' | 'studentIds', value: any) => {
    stepTwoFormDispatch.onFormChange(name, value);
  };

  const stepOneValidation = async nextStep => {
    const activeId = state.activeFormId || state.lastActiveFormId;
    const errors = await stepOneFormDispatch.validateSingleForm(
      activeId,
      stepOneFormState.form?.[activeId]
    );
    const form = Object.values(stepOneFormState.form) as any[];
    const assignedDays = form.filter(x => x.assigned === true) as any[];

    const assignedHasActivities = assignedDays.filter(
      x => x?.lesson?.sections?.filter(s => s.activities.length > 0).length > 0
    ).length;

    const validationsOnAssigned: any = assignedDays.find(
      x => x.dueDate === '' || x.dueDate === 'Invalid date'
    );

    if (validationsOnAssigned) {
      setActiveForm(validationsOnAssigned.id);
      return;
    }
    const allowedAssignedDuration = assignedDays.some(x => calcDuration(x as any) <= 30);

    if (
      !errors &&
      assignedDays.length &&
      assignedHasActivities === assignedDays.length &&
      allowedAssignedDuration
    ) {
      stepperDispatch.completeActiveStepAndSetNextStep(nextStep);
      return;
    }

    stepperDispatch.onStepIncomplete(nextStep);
    return;
  };

  const stepTwoValidation = nextStep => {
    const hasSelectedClassroom = stepTwoFormState.form?.classroomIds.length > 0;

    if (hasSelectedClassroom) {
      stepperDispatch.completeActiveStepAndSetNextStep(nextStep);
      return;
    }
    stepperDispatch.onStepIncomplete(nextStep);
    return;
  };

  const completeActiveStepAndSetNextStep = async (nextStep: number): Promise<void> => {
    switch (stepperState.activeStep) {
      case 1:
        stepOneValidation(nextStep);
        break;

      case 2:
        stepTwoValidation(nextStep);
        break;
    }
  };

  const getLastActiveFormId = () => {
    return state.lastActiveFormId;
  };

  const setForm = (nextFormValue, options = {}) => {
    // CSC - FIX ME
    if (!nextFormValue.length) return;

    const startDate = nextFormValue?.[0]?.startDate ?? '';
    const endDate = nextFormValue?.[nextFormValue.length - 1]?.startDate ?? '';

    dispatch({
      type: 'SET_DATE_RANGE',
      payload: `${formatUiDate(new Date(startDate))} - ${formatUiDate(new Date(endDate))}`
    });

    stepOneFormDispatch.setForm(nextFormValue, options);
  };

  const isAssignmentRemovable = false; // TODO: compute
  const isAssignmentEditable = false; // TODO: compute

  const setClassrooms = (classrooms: any[]) => {
    dispatch({ type: 'SET_ALL_CLASSROOMS_AND_STUDENTS', payload: { classrooms } });
  };

  const setPreselectedClassroomsAndStudents = (classroomIds: number[], studentIds: number[]) => {
    stepTwoOnMultiSelectFormChange('classroomIds', classroomIds);
    stepTwoOnMultiSelectFormChange('studentIds', studentIds);
  };

  const handleDateChange = (name, value) => {
    const date = formatFullDate(value);
    stepOneFormDispatch.onFormChange(state.activeFormId as string)(name, date);
  };

  return (
    <LessonAssignmentContext.Provider
      value={[
        {
          stepperState,
          stepOneFormState,
          stepTwoFormState,
          lessonAssignmentState: {
            ...state,
            isAssignmentRemovable,
            isAssignmentEditable
          }
        },
        {
          stepperDispatch: {
            ...stepperDispatch,
            completeActiveStepAndSetNextStep,
            getLastActiveFormId
          },
          stepOneFormDispatch: {
            ...stepOneFormDispatch,
            setForm,
            onMoveActivity,
            onAddActivity,
            onRemoveActivity,
            onUnassignActiveForm,
            handleAssign,
            onRemoveAllActivities,
            onFormChange: stepOneOnFormChange,
            onRemoveFileAdded,
            handleDateChange
          },
          stepTwoFormDispatch: {
            ...stepTwoFormDispatch,
            onMultiSelectFormChange: stepTwoOnMultiSelectFormChange,
            setPreselectedClassroomsAndStudents
          },
          lessonAssignmentDispatch: {
            setActiveForm,
            toggleEditTitle,
            onRemoveAssignment,
            getAssignmentPayload,
            getTeacherInstructionPayload,
            setSelectedClassroomsAndStudents,
            setClassrooms
          }
        }
      ]}
    >
      {props.children}
    </LessonAssignmentContext.Provider>
  );
};
